<template>
    <main v-if="simManager">
        <template v-if="isWeb">
            <router-link to="/user/dashboard?tab=sim" class="back_icon">
                <span class="icon icon-arrow-left-1 "></span>
            </router-link>
        </template>
        <section>
            <SimInfoSession
                :deviceInfo="deviceInfo"
                :simManager="simManager"
                :subDevice="subDevice"
            />
        </section>
        <CountryLimitCard
            :simManager="simManager"
            @switchPlan="handleSwitchPlan"
            @refresh="getSimManager"
            v-if="simManager.isCardSuspendForCountry"
        />
        <section>
            <SubscriptionCard
                :simManager="simManager"
                :subDevice="subDevice"
                :boundStatus="boundStatus"
                :from="from"
                @refresh="getSimManager"
                ref="subscriptionCardRef"
            />
        </section>
        <section>
            <DataUsageCard
                :trafficData="simManager.trafficData"
                :subId="simManager.currentSubscription?.id"
            />
        </section>
        <AiSwitchSection
            v-if="isLocked"
            :deviceInfo="deviceInfo"
            :show-ai="showAi"
            :subDevice="subDevice"
            :simManager="simManager"
            :hasCurrentPlan="hasCurrentPlan"
        />
    </main>
</template>

<script lang="ts">
import Vue from 'vue';
import SubscriptionCard from './components/SubscriptionCard.vue';
import SimInfoSession from './components/SimInfoSession.vue';
import { getYmhFromQuery } from '@/common/js/query';
import { getSimCard } from '@/api/sim';
import DataUsageCard from './components/DataUsageCard.vue';
import { BoundStatus, ESimCardModule, ICloudService, IReolinkDevice, ISubDevice } from '@reolink-cloud/interface';
import { SimManager } from '@/lib/simManager';
import { productsManager } from '@/lib';
import { mapGetters } from 'vuex';
import { unfoldError } from '@/common/js/notice';
import nProgress from 'nprogress';
import { config } from 'CONFIG';
import { EPlatform } from '@/api/app';
import { getIccidByUid, getSubDeviceList } from '@/api/subscriptions';
import AiSwitchSection from 'VIEW/user/MyDevice/components/AiSwitchSection.vue';
import { getDeviceList } from '@/api/cloud';
import { Async } from '@reolink-web/utils';
import { isValidIccid } from '@/utils/business';
import CountryLimitCard from './components/CountryLimitCard.vue';

export default Vue.extend({
    components: {
        AiSwitchSection,
        SubscriptionCard,
        SimInfoSession,
        DataUsageCard,
        CountryLimitCard
    },
    data() {

        return {
            simManager: null as SimManager | null,
            deviceInfo: {} as IReolinkDevice,
            subDevice: undefined as ISubDevice | undefined,
            boundStatus: undefined as BoundStatus | undefined,
            uid: '' as string,
        };

    },
    async created() {

        try {

            await this.getSimManager();
            await this.getLockedDevice();

        } catch ( e ) {

            console.error( e );
            unfoldError( 'errors.server_error' );

        }

    },
    mounted() {

        nProgress.done();

    },
    computed: {
        ...mapGetters<'lang'>( ['lang'] ),
        isWeb(): boolean {

            return config.client === EPlatform.Web;

        },
        isLocked(): boolean {

            return !!this.simManager?.data.esimDevice;

        },
        isCountryAiOpen(): boolean {

            return config.aiFeatures.length > 0;

        },
        currentSubscription(): ICloudService | undefined {

            return this.simManager?.currentSubscription;

        },
        from(): string {

            return this.$route.query['from'] as string ?? '';

        },
        planSupportAi(): boolean {

            if ( !config.aiFeatures.some( item => this.currentSubscription?.planInfo?.style[`cloud_ai.${item}`] )) {

                return false;

            }
            return true;

        },
        showAi(): boolean {

            if ( !this.isLocked || this.boundStatus === 'unbound'  ) return false;
            if ( this.simManager?.isRetired ) return false;

            // 1. 无套餐 / 套餐过期 → 国家开放显示,不开放隐藏
            if ( !this.currentSubscription ) return this.isCountryAiOpen;

            const subscriptionActive = this.currentSubscription?.effectiveStatus !== 'inactive';

            if ( this.planSupportAi ) {

                // 2. 设备有支持AI的套餐 -> 未过期->显示 | 过期-> 国家开放显示,不开放隐藏
                return subscriptionActive ? true : this.isCountryAiOpen;

            } else {

                // 3. 设备有不支持AI的套餐 -> 国家开放显示,不开放隐藏
                return this.isCountryAiOpen;

            }

        },

        hasCurrentPlan(): boolean {

            return !!this.simManager?.currentSubscription;

        },
    },
    methods: {
        async getLockedDevice() {

            if ( this.uid ) { // 锁卡设备

                const [deviceList, subDeviceList] = await Promise.all( [
                    getDeviceList(),
                    getSubDeviceList(),
                ] );
                const device = deviceList.find( item => item.uid === this.uid );
                const subDevice = subDeviceList.find( item => item.uid === this.uid );
                this.boundStatus = device?.status;
                if ( !device ) {

                    unfoldError( 'errors.server_error' );
                    setTimeout(() => {

                        Async.invokeAsync( async () => {

                            await this.$router.push({ path: '/user/my-devices' });

                        });

                    }, 1000 );
                    return;

                }
                this.deviceInfo = device;
                this.subDevice = subDevice;

            }

        },
        async getSimManager() {

            let iccid = getYmhFromQuery( this.$route );
            if ( !isValidIccid( iccid )) {

                const esimProfile = await getIccidByUid();
                iccid = esimProfile.find( item => item.uid === iccid )?.iccid ?? '';
            
            }

            const [rawSimData] = await Promise.all( [
                getSimCard({
                    iccid,
                    includes: [ESimCardModule.CLOUD_SERVICES, ESimCardModule.LAST_USED_DEVICE, ESimCardModule.SIM_CARD],
                    checkActionConfig: true,
                }),
                productsManager.update({ lang: this.lang })
            ] );

            const simManager = new SimManager( rawSimData, rawSimData.services );
            await simManager.init({ trafficData: true });
            this.simManager = simManager;
            this.uid = rawSimData.esimDevice;

        },
        handleSwitchPlan() {

            const subscriptionCardRef = this.$refs.subscriptionCardRef as InstanceType<typeof SubscriptionCard>;
            if ( subscriptionCardRef ) {

                subscriptionCardRef.handleMoreMenuAction( 'switch' );

            }

        }

    }
});
</script>

<style lang="less" scoped>
main{
    max-width: 528px;
    margin: auto;
    margin-top: 20px;
    margin-bottom: 68px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    @media @mobile{
        padding: 0 20px;
        margin: 20px auto;
        gap: 16px;
    }
    .icon{
        font-size: 20px;
        color: @black;
        &:hover{
            color: @b_6;
        }
    }
}
</style>
